import { PencilIcon } from '@heroicons/react/outline';
import { Checkbox, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { DefaultTheme, makeStyles, Styles } from '@mui/styles';
import { GridColDef } from '@mui/x-data-grid-pro';
import axios from 'axios';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';

import { fedexRedirect, getOptions, gridDateTimeFormatter, withQueryParams } from '@shared/commonFunctions';
import { IDraggableItem } from '@shared/components/draggable/DraggableList';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { LightTooltip } from '@shared/components/grid/GridCell';
import { gridRowActionsIconProps } from '@shared/components/grid/GridColActions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { SERVICES } from '@shared/constants/apiConfig';
import { Roles } from '@shared/constants/constants';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { SaveButton } from '@shared/inputs/Buttons';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { GroupUser } from '@shared/interfaces';
import { FieldGroup, FieldWrapper, SubHeaderWrapper } from '@styles/Admin-tools/Tracker';
import { Link, ViewHeightWrapper } from '@styles/CommonStyles';
import { colorTheme, extraColor } from '@styles/muiTheme';

import { useAppSelector } from '@store/hooks';
import { selectCurrentState, updateCurrentState } from '@store/reducers/screenStateSlice';
import { useTabs } from '@shared/hooks/useTabs';
import { withOpenTab } from '@shared/HOCs/withOpenTab';
import { INoteModal, ISortOrderFormValues, ITracker } from './interfaces';
import { NoteModal } from './NoteModal';
import { requestChangeSortOrder, requestUpsertNote } from './request';
import { SortOrderModal } from './SortOrderModal';
const APP_REQUEST_DECLINED = 'App Request Declined';
const DEFAULT_PAGE_SIZE = 100;

const Notes = ({ row, handleEditClick }: { row: ITracker; handleEditClick: (data: ITracker) => void }) => {
  return (
    <Stack direction="row">
      <IconButton onClick={() => handleEditClick(row)}>
        <PencilIcon {...gridRowActionsIconProps} />
      </IconButton>
      <LightTooltip title={row.note || ''} placement="top">
        <Typography>{row.note}</Typography>
      </LightTooltip>
    </Stack>
  );
};

const defaultValue = 'temp';
const gridName = 'Tracker';

const extraOption = {
  value: defaultValue,
  label: 'My Team`s Leads'
};

const accountingHoldClass = {
  backgroundColor: extraColor.palette.error.light,
  color: `${colorTheme.palette.common.white} !important`
};

const TrackerPage = () => {
  const { userHasRoles, isAdmin, userRoles} = useUserAccount();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openTab } = useTabs();
  const formikRef = useRef<FormikProps<ISortOrderFormValues>>(null);
  const savedValue = useAppSelector(selectCurrentState(gridName));

  const [includedFundedLeads, setIncludedFundedLeads] = useState(false);
  const [selectedUser, setSelectedUser] = useState(defaultValue);
  const [isLeadOrderModalOpen, setLeadOrderModalOpen] = useState(0);
  const [noteModalOpen, setNoteModalOpen] = useState<INoteModal | null>(null);
  const [noteModalPosition, setNoteModalPosition] = useState(0);
  const [loading, setLoading] = useState(false);
  // const [showUserDropdown, setShowUserDropdown] = useState(false);

  // useEffect(() => {
  //   if (isAdmin || userHasRoles([Roles.LC_LEAD, Roles.LC_MANAGER])) {
  //     setShowUserDropdown(true);
  //   }
  //   else {
  //     setShowUserDropdown(false);
  //   }
  // },  [isAdmin, userRoles, isAccountLoading]);
  const showUserDropdown = useMemo(
    () => {
        if (isAdmin || userHasRoles([Roles.LC_LEAD, Roles.LC_MANAGER])) {
          return true;
        }
      return false;
    },
    [isAdmin, userRoles]
  );

  const activeUser = useMemo(() => savedValue || selectedUser, [selectedUser, savedValue]);

  const { data: users = [] } = useSWR<GroupUser[]>(`${SERVICES.USER}/users/alltracker`, { revalidateOnMount: undefined });

  const { data: sortOrderList = [], mutate: refreshSortOrderList } = useSWR<IDraggableItem[]>(`${SERVICES.WORKFLOW}/trkr/sortitems`, { revalidateOnMount: undefined });

  const { data: rows, error, isValidating, mutate: refreshTracker } = useSWR(withQueryParams(`${SERVICES.APICOMPOSER}/v1/trkr/items`, {
    userId: activeUser !== defaultValue ? activeUser : null,
    includedFundedLeads,
  }), (url: string) => axios.post(url), { revalidateOnMount: undefined });

  const isLoading = useMemo(() => (!rows?.data && !error) || isValidating, [rows, error, isValidating]);

  const rowClasses = useMemo(() => {
    const classesMap = {} as Record<string, unknown>;
    sortOrderList.forEach((item) => {
      if (item.id) {
        classesMap[item.id.toString()] = {
          backgroundColor: item.backgroundHexColor?.trim(),
          color: `${item.textHexColor?.trim()} !important`,
        };
      }
    });

    classesMap['accountingHoldClass'] = accountingHoldClass;

    return classesMap;
  }
    , [sortOrderList]);

  const useStyles = makeStyles(rowClasses as Styles<DefaultTheme, object, string>);

  const classes = useStyles();

  const handleEditClick = (row: ITracker) => {
    const data = {
      dealId: row.dealId,
      note: row.note
    };

    setNoteModalOpen(data);
    setNoteModalPosition(prevState => prevState + 1);
  };

  const columns: GridColDef[] = useMemo(() => [
    dataGridCol('firstName', { minWidth: 120 }),
    dataGridCol('lastName', { minWidth: 120 }),
    dataGridCol('assetTag', {
      headerName: 'Asset',
      minWidth: 150,
      maxWidth: 500
    }),
    dataGridCol('assignedLc', { headerName: 'LC' }),
    dataGridCol('lenderName', { headerName: 'CU' }),
    dataGridCol('estimatedGross', {
      headerName: 'Est Gross ($)',
      ...gridCurrencyCell,
    }),
    dataGridCol('vscMbi', {
      headerName: 'VSC/MBI',
      minWidth: 80,
      renderCell: (params) => {
        const { hasVsc, hasMbi } = params.row;
        return hasVsc === 'Y' || hasMbi === 'Y' ? 'Y' : 'N';
      },
    }),
    dataGridCol('hasGap', {
      headerName: 'GAP',
      flex: 0.5
    }),
    dataGridCol('hasPaf', {
      headerName: 'PAF',
      flex: 0.5
    }),
    dataGridCol('hasCom', {
      headerName: 'COM',
      flex: 0.5
    }),
    dataGridCol('hasMba', {
      headerName: 'MBA',
      flex: 0.5
    }),
    dataGridCol('hasVrp', {
      headerName: 'VRP',
      flex: 0.5
    }),
    dataGridCol('state', {
      headerName: 'St',
      flex: 0.5
    }),
    dataGridCol('leadSource', {
      headerName: 'Source',
      minWidth: 130
    }),
    dataGridCol('downPayment', {
      headerName: 'DP ($)',
      ...gridCurrencyCell
    }),
    dataGridCol('totalAmountFinanced', { headerName: 'TAF ($)', ...gridCurrencyCell }),
    dataGridCol('trackerStage', { headerName: 'Stage' }),
    dataGridCol('trackerStep', { headerName: 'Step' }),
    dataGridCol('receivedDate', {
      headerName: 'Received',
      type: 'dateTime', 
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('updatedDate', {
      headerName: 'Updated',
      type: 'dateTime', 
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('trackingIn', {
      headerName: 'Trk In',
      renderCell: (params) =>
        <Link onClick={() => fedexRedirect(params.row.trackingIn)}>
          {params.row.trackingIn || ''}
        </Link>
    }),
    dataGridCol('trackingOut', {
      headerName: 'Trk Out',
      renderCell: (params) =>
        <Link onClick={() => fedexRedirect(params.row.trackingOut)}>
          {params.row.trackingOut || ''}
        </Link>
    }),
    dataGridCol('fundedDate', {
      type: 'dateTime', 
      valueFormatter: gridDateTimeFormatter,
    }),
    dataGridCol('note', {
      minWidth: 350,
      renderCell: (params) => <Notes row={params.row} handleEditClick={handleEditClick} />,
    }),
  ], [handleEditClick]);

  const onMoveAdjustment = useCallback((dropResult: DropResult, moveFunc: (from: number, to: number) => void) => {
    if (!formikRef.current) return;

    moveFunc(dropResult.source.index, dropResult.destination?.index ?? 0);
    formikRef.current.setFieldTouched('leadSortOrder', true);
  }, [formikRef.current]);

  const onOrderApply = useCallback(async (data: ISortOrderFormValues) => {
    setLoading(true);
    const sortIds = data.leadSortOrder.map((item) => item.id);
    await requestChangeSortOrder(sortIds as number[]);
    await refreshSortOrderList();
    await refreshTracker();
    setLeadOrderModalOpen(0);
    setLoading(false);
  }, []);

  const onNoteModalSubmit = useCallback(async (data: INoteModal) => {
    setLoading(true);
    await requestUpsertNote(data.dealId, data.note);
    await refreshTracker();
    setNoteModalOpen(null);
    setNoteModalPosition(0);
    setLoading(false);
  }, []);

  const handleChange = useCallback((user: string) => {
    // resetting tracker data for that user to do reload
    refreshTracker(undefined, {revalidate: false});
    dispatch(updateCurrentState({ screenName: gridName, screenState: user }));
    setSelectedUser(user);
  }, []);

  const getClass = useCallback((row: ITracker) => 
    classes[row.isAccountingHold && row.minTrkrStepName !== APP_REQUEST_DECLINED 
    ? 'accountingHoldClass' : row.sortOrder.toString()], [classes]);

  const handleRowClick = useCallback(({ row }: { row: ITracker }, event: React.SyntheticEvent<HTMLElement>) => {
    const targetElement = (event.target as HTMLElement).tagName;

    if (!['path', 'A', 'svg'].includes(targetElement)) {
      const route = withQueryParams('/contact-manager', {
        id: row.contactId,
        tab: 3,
        dealId: row.dealId,
      });
      const title = `${row.firstName} ${row.lastName}`;
      openTab({ title, route });
      navigate(route);
    }
  }, []);

  const handleClose = useCallback(() => {
    setNoteModalOpen(null);
    setNoteModalPosition(0);
  }, []);

  const subHeader = useMemo(
    () => (
      <SubHeaderWrapper>
        <Typography
          variant="h3"
          color={theme.palette.primary.main}
          mb={2}
          mt='16px'
        >
          Tracker
        </Typography>

        <FieldGroup>
          <FieldWrapper margin="0 5px 0 0">
            <Checkbox
              onChange={(e) => setIncludedFundedLeads(e.target.checked)}
            />
            <Typography>Funded Leads</Typography>
          </FieldWrapper>

          <SaveButton
            sx={{ margin: '0 15px' }}
            type="button"
            label="Lead Sort Order"
            onClick={() => setLeadOrderModalOpen(prevState => prevState + 1)}
          />

          {
          showUserDropdown &&  ( 
            <FieldWrapper width="230px">
              <SelectAutocomplete
                placeholder="User"
                value={activeUser}
                options={[extraOption, ...(getOptions(users, 'userId', 'userName'))]}
                onChange={handleChange}
              />
            </FieldWrapper> )
          }
        </FieldGroup>
      </SubHeaderWrapper>
    ),
    [users, includedFundedLeads, selectedUser, showUserDropdown, savedValue]
  );
  
  return (
    <ViewHeightWrapper>
      <PhoenixGrid
        canNotCopy
        canNotEdit
        withHeader
        noButton
        showClearAllFilter
        clearAllFilterBtnSx = {{position: 'absoulte', top: '10px', left: '80px'}}
        pageSize={DEFAULT_PAGE_SIZE}
        rowsPerPageOptions={[20, 50, 100]}
        name=''
        gridId={gridName + activeUser}
        rows={isLoading ? [] : rows?.data}
        columns={columns}
        loading={isLoading}
        pinnedLeft={['firstName', 'lastName', 'assetTag']}
        withSubheader={subHeader}
        getRowId={(row) => row.dealId}
        getRowClassName={(params) => getClass(params.row)}
        getCellClassName={(params) => getClass(params.row)}
        onRowClick={handleRowClick}
      />

      {!!isLeadOrderModalOpen && (
        <SortOrderModal
          formikRef={formikRef}
          sortModalPosition={isLeadOrderModalOpen}
          listItems={sortOrderList}
          sortModalLoading={loading}
          onClose={() => setLeadOrderModalOpen(0)}
          onOrderApply={onOrderApply}
          onMoveAdjustment={onMoveAdjustment}
        />
      )}

      {!!noteModalOpen && (
        <NoteModal
          row={noteModalOpen}
          noteModalPosition={noteModalPosition}
          notesModalLoading={loading}
          onSubmit={onNoteModalSubmit}
          onClose={handleClose}
        />
      )}
    </ViewHeightWrapper>
  );
};

export const Tracker = withOpenTab(TrackerPage, 'Tracker');

